import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Label, Button, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink } from "@/Components/ui";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import MyEditor from "../../../Components/editor/MyEditor";
import tmp_img from "../../../assets/images/voucher_tmp/부산-logo.png";
import { API } from "../../../api/client";
import Select from "react-select";
import { SITE_ID, SUBDOMAIN } from "../../../assets/meta/meta";
import { voucherService } from "@/api/services/voucher.service";
import { findMissingRequiredFields, focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { dialogService } from "@/utils/dialogService";
import { cn } from "@/lib/utils";
import { DEFAULT_UNSAVED_CHANGES_MESSAGE, useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
const VoucherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCopyMode = searchParams.get("copy") === "true";
  const requestedTab = searchParams.get("tab");
  const fileInputRef = React.useRef(null);
  const [name, setName] = React.useState("");
  const [expireDate, setExpireDate] = React.useState(null);
  const [precautions, setPrecautions] = React.useState("");
  const [autoSend, setAutoSend] = React.useState(false);
  const [autoSendDate, setAutoSendDate] = React.useState(null);
  const [image, setImage] = React.useState(null);
  const [isGiftable, setIsGiftable] = React.useState(false);
  const [prevImage, setPrevImage] = React.useState(null); // 수정 시 기존 이미지
  const [eventSites, setEventSites] = React.useState([]);
  const [voucherBookRules, setVoucherBookRules] = React.useState([]);
  const [bookRuleLoading, setBookRuleLoading] = React.useState(false);
  const [editingBookRuleId, setEditingBookRuleId] = React.useState<number | null>(null);
  const [bookStartDate, setBookStartDate] = React.useState(new Date());
  const [bookEndDate, setBookEndDate] = React.useState(new Date());
  const [bookState, setBookState] = React.useState("노출중");
  const [bookStock, setBookStock] = React.useState(1);
  const [bookType, setBookType] = React.useState("event");
  const [bookUsageAmount, setBookUsageAmount] = React.useState(0);
  const [bookUsagePeriod, setBookUsagePeriod] = React.useState(30);
  const [selectedBookSite, setSelectedBookSite] = React.useState<any>({
    value: SITE_ID,
    label: "현재 사이트",
  });
  const voucherId = Number(id || 0);
  const canManageBookRules = voucherId !== 0 && !isCopyMode;
  const webBaseUrl = React.useMemo(() => {
    const domain = (window as any).AMZ_ENV.REACT_APP_DOMAIN || "amazingticket.site";
    return `https://${SUBDOMAIN}.${domain}`;
  }, []);
  const currentBookSiteOption = React.useMemo(() => eventSites.find((site) => site.value === SITE_ID) || {
    value: SITE_ID,
    label: "현재 사이트",
  }, [eventSites]);
  const [activeTab, setActiveTab] = React.useState("basic");
  const [bookRuleModalOpen, setBookRuleModalOpen] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [hasUnsavedBookRuleChanges, setHasUnsavedBookRuleChanges] = React.useState(false);
  const markDirty = React.useCallback(() => setHasUnsavedChanges(true), []);
  const markBookRuleDirty = React.useCallback(() => setHasUnsavedBookRuleChanges(true), []);
  const { confirmDiscardChanges } = useUnsavedChangesGuard({
    isDirty: hasUnsavedChanges || hasUnsavedBookRuleChanges,
    confirmNavigation: dialogService.confirm,
  });
  const handleImageFiles = (e) => {
    const files = e.target.files;
    if (files.length !== 0) {
      markDirty();
      setImage(files[0]);
    }
  };
  React.useEffect(() => {
    if (id !== "0") {
      API.get(`/voucher/${id}/`)
        .then(async (response) => {
          const voucherData = response?.data;
          if (!voucherData || typeof voucherData !== "object" || Array.isArray(voucherData)) {
            await dialogService.handleAlert("교환권 정보를 불러오지 못했습니다.", { force: "error" });
            return;
          }
          fillStates(voucherData, isCopyMode);
        })
        .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    }
  }, [id, isCopyMode]);
  React.useEffect(() => {
    API.get(`/sites/camps/`)
      .then((response) => {
        const sites = (response.data || []).map((site) => ({
          value: site.id,
          label: site.name,
        }));
        setEventSites(sites);
        const defaultSite = sites.find((site) => site.value === SITE_ID);
        if (defaultSite) {
          setSelectedBookSite(defaultSite);
        }
      })
      .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
  }, []);
  const resetBookRuleForm = () => {
    setEditingBookRuleId(null);
    setBookStartDate(new Date());
    setBookEndDate(new Date());
    setBookState("노출중");
    setBookStock(1);
    setBookType("event");
    setBookUsageAmount(0);
    setBookUsagePeriod(30);
    setSelectedBookSite(currentBookSiteOption);
  };
  const fetchVoucherBookRules = React.useCallback(async () => {
    if (!canManageBookRules) {
      setVoucherBookRules([]);
      return;
    }
    setBookRuleLoading(true);
    try {
      const response = await voucherService.listVoucherBookRules();
      const rules = (response.data || []).filter((rule) => rule.voucher_product?.id === voucherId);
      setVoucherBookRules(rules);
    }
    catch (error) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
    finally {
      setBookRuleLoading(false);
    }
  }, [canManageBookRules, voucherId]);
  React.useEffect(() => {
    fetchVoucherBookRules();
  }, [fetchVoucherBookRules]);
  React.useEffect(() => {
    if (requestedTab === "book" && canManageBookRules) {
      setActiveTab("book");
    }
  }, [canManageBookRules, requestedTab]);
  React.useEffect(() => {
    if (!canManageBookRules && activeTab !== "basic") {
      setActiveTab("basic");
    }
  }, [activeTab, canManageBookRules]);
  const handleEditBookRule = (rule) => {
    setEditingBookRuleId(rule.id);
    setBookStartDate(rule.start_date);
    setBookEndDate(rule.end_date);
    setBookState(rule.state);
    setBookStock(rule.stock);
    setBookType("event");
    setBookUsageAmount(0);
    setBookUsagePeriod(30);
    setSelectedBookSite(currentBookSiteOption);
  };
  const discardBookRuleForm = () => {
    setBookRuleModalOpen(false);
    resetBookRuleForm();
    setHasUnsavedBookRuleChanges(false);
  };
  const confirmBookRuleClose = async () => {
    if (hasUnsavedBookRuleChanges && !await dialogService.confirm(DEFAULT_UNSAVED_CHANGES_MESSAGE))
      return;
    discardBookRuleForm();
  };
  const openCreateBookRuleModal = () => {
    resetBookRuleForm();
    setHasUnsavedBookRuleChanges(false);
    setBookRuleModalOpen(true);
  };
  const openEditBookRuleModal = (rule) => {
    handleEditBookRule(rule);
    setHasUnsavedBookRuleChanges(false);
    setBookRuleModalOpen(true);
  };
  const openVoucherBookEventPage = (ruleId: number) => {
    window.open(`${webBaseUrl}/event-voucher/${ruleId}`, "_blank", "noopener,noreferrer");
  };
  const saveBookRule = async () => {
    if (!canManageBookRules) {
      await dialogService.handleAlert("상품 저장 후 규칙을 설정할 수 있습니다.");
      return;
    }
    if (!bookStartDate || !bookEndDate) {
      await dialogService.handleAlert("필수 입력값(기간/사이트)을 입력해주세요.");
      return;
    }
    const payload = {
      voucher_product: voucherId,
      start_date: getDate(bookStartDate as any),
      end_date: getDate(bookEndDate as any),
      state: bookState,
      stock: Number(bookStock),
      type: "event",
      usage_amount: 0,
      usage_period_day: 0,
      site: currentBookSiteOption.value,
    };
    try {
      const successMessage = editingBookRuleId ? "무료쿠폰북 규칙이 수정되었습니다." : "무료쿠폰북 규칙이 생성되었습니다.";
      if (editingBookRuleId) {
        await voucherService.updateVoucherBookRule(editingBookRuleId, payload);
      }
      else {
        await voucherService.createVoucherBookRule(payload);
      }
      setHasUnsavedBookRuleChanges(false);
      await dialogService.handleAlert(successMessage);
      discardBookRuleForm();
      await fetchVoucherBookRules();
    }
    catch (error) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  };
  const deleteBookRule = async (ruleId: number) => {
    if (!await dialogService.confirm("해당 무료쿠폰북 규칙을 삭제하시겠습니까?"))
      return;
    try {
      await voucherService.deleteVoucherBookRule(ruleId);
      await dialogService.handleAlert("무료쿠폰북 규칙이 삭제되었습니다.");
      if (editingBookRuleId === ruleId) {
        discardBookRuleForm();
      }
      await fetchVoucherBookRules();
    }
    catch (error) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  };
  const fillStates = (data, copyMode = false) => {
    const safeName = data?.["name"] || "";
    setName(copyMode ? `${safeName} (복사)` : safeName);
    setExpireDate(data?.["expire_date"] ?? null);
    setPrecautions(data?.["precautions"] || "");
    setAutoSend(Boolean(data?.["auto_send"]));
    setAutoSendDate(data?.["auto_send_date"] ?? null);
    setIsGiftable(Boolean(data?.["giftable"]));
    if (!copyMode) {
      setPrevImage(data?.["image"] ?? null);
    }
  };
  const sendData = async () => {
    const data = await checkData();
    if (data === null)
      return;
    let createdId = null;
    API.post('/voucher/', data, {
      headers: { "Content-Type": "multipart/form-data" }
    })
      .then(async (response) => {
        createdId = response.data?.id;
        setHasUnsavedChanges(false);
        await dialogService.handleAlert("교환권이 등록되었습니다.");
        if (createdId) {
          navigate(`/voucher-form/${createdId}`);
        }
      })
      .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
  };
  const updateData = async () => {
    const data = await checkData();
    if (data === null)
      return;
    API.put(`/voucher/${id}/`, data, {
      headers: { "Content-Type": "multipart/form-data" }
    })
      .then(async () => {
        setHasUnsavedChanges(false);
        await dialogService.handleAlert("교환권이 수정되었습니다.");
        navigate('/voucher-list');
      })
      .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
  };
  const getDate = (dateObj) => {
    if (!dateObj)
      return "";
    if (typeof dateObj === "string")
      return dateObj;
    const date = Array.isArray(dateObj) ? dateObj[0] : dateObj;
    if (!(date instanceof Date) || Number.isNaN(date.getTime()))
      return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const checkData = async () => {
    const missingRequiredFields = findMissingRequiredFields([
      { label: "교환권명", value: name, elementId: "name" },
      { label: "유효기간", value: expireDate, elementId: "expireDateField" },
      { label: "주의사항", value: precautions, elementId: "precautionsEditor" },
    ]);
    if (missingRequiredFields.length > 0) {
      await dialogService.handleAlert(formatRequiredMessage(missingRequiredFields));
      focusFirstMissingField(missingRequiredFields);
      return null;
    }
    let data = new FormData();
    data.append("name", name);
    // image default value 작동 안 됨.
    if (image)
      data.append("image", image === null ? new File([tmp_img], "camp-logo.png", { type: `image/png` }) : image);
    data.append("precautions", precautions);
    data.append("expire_date", getDate(expireDate));
    data.append("auto_send", String(autoSend));
    data.append("giftable", String(isGiftable));
    if (autoSendDate)
      data.append("auto_send_date", getDate(autoSendDate));
    return data;
  };
  return (<React.Fragment>
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="교환권 등록 및 수정" pageTitle="교환권" />

        <Row className="g-4 mb-3">
          <div className="col-sm-auto">
            <div>
              <h4 className="card-title mb-0 flex-grow-1">등록 및 수정</h4>
            </div>
          </div>
          <div className="col-sm-3 ms-auto">
            <div className="d-flex justify-content-sm-end gap-2">
              {(id !== "0" && !isCopyMode) ? (<Button color="success" className="add-btn me-1" onClick={updateData} id="create-btn">
                수정
              </Button>) : (<Button color="success" className="add-btn me-1" onClick={sendData} id="update-btn">
                생성
              </Button>)}
              <Button color="danger" className="add-btn me-1" onClick={async () => {
                if (!await confirmDiscardChanges())
                  return;
                navigate(`/voucher-list`);
              }} id="cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        </Row>

        {canManageBookRules ? (
          <Row>
            <Col xxl={12}>
              <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                <div className="card-body p-0">
                  <Nav tabs className="nav-tabs-custom nav-success border-0 px-4">
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "basic"
                        ? "border-green-500 text-green-600 active"
                        : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => setActiveTab("basic")}>
                        기본정보
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "book"
                        ? "border-green-500 text-green-600 active"
                        : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => setActiveTab("book")}>
                        배포(쿠폰북)
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>
              </div>
            </Col>
          </Row>
        ) : null}

        {activeTab === "basic" ? (
          <>
            <Row>
              <Col xxl={12}>
                <Card>
                  <CardBody onChangeCapture={markDirty} onInputCapture={markDirty}>
                    <div className="live-preview">
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label htmlFor="firstNameinput" className="form-label">
                              교환권명
                            </Label>
                            <Input type="text" className="form-control" placeholder="교환권명" id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div>
                            <Label htmlFor="formFile" className="form-label">
                              교환권이미지
                            </Label>
                            <Input className="form-control" type="file" id="formFile" accept="image/jpg, image/jpeg, image/png" ref={fileInputRef} onChange={handleImageFiles} />
                          </div>
                          {prevImage && !image ? (<div className="mb-3">
                            <img src={prevImage} alt="이미지" style={{ width: "120px", height: "auto" }} />
                          </div>) : null}
                        </Col>
                        <Col md={4}>
                          <Label htmlFor="ForminputState" className="form-label">
                            유효기간
                          </Label>
                          <CommonDatePicker value={expireDate} onChange={(value) => {
                            markDirty();
                            setExpireDate(value);
                          }} id="expireDateField" className="form-control" options={{
                          }} />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Input style={{ marginRight: 5 }} className="form-check-input" type="checkbox" id="formCheck6" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
                          <Label className="form-check-label" htmlFor="formCheck6">
                            가입시지급
                          </Label>
                        </Col>
                        {autoSend ? (<Col md={4}>
                          <Label htmlFor="ForminputState" className="form-label">
                            지급기간
                          </Label>
                          <CommonDatePicker value={autoSendDate} onChange={(value) => {
                            markDirty();
                            setAutoSendDate(value);
                          }} className="form-control" options={{
                          }} />
                        </Col>) : null}
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Input style={{ marginRight: 5 }} className="form-check-input" type="checkbox" id="formCheck7" checked={isGiftable} onChange={(e) => setIsGiftable(e.target.checked)} />
                          <Label className="form-check-label" htmlFor="formCheck7">
                            교환권 선물 가능여부
                          </Label>
                        </Col>
                      </Row>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col xxl={12}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">주의사항</h4>
                  </CardHeader>
                  <CardBody>
                    <div id="precautionsEditor" tabIndex={-1}>
                      <MyEditor value={precautions} onChange={(value) => {
                        markDirty();
                        setPrecautions(value);
                      }} noImage={true} />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}

        {canManageBookRules && activeTab === "book" ? (
          <>
            <Row>
              <Col xxl={12}>
                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                  <div className="card-header bg-white border-b border-gray-100 px-6 py-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="mb-1 text-lg font-semibold text-gray-900">무료쿠폰북 규칙 (서브리스트)</h4>
                      <span className='text-sm text-gray-500'>등록 시 쿠폰북 다운로드 페이지에서 다운로드 가능합니다.</span>
                    </div>
                    <Button color="success" className="rounded-lg px-5 h-10 text-sm font-semibold" onClick={openCreateBookRuleModal}>
                      규칙 생성
                    </Button>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">ID</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">기간</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">상태</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">타입</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">수량</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">사이트</th>
                            <th className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">동작</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookRuleLoading ? (
                            <tr>
                              <td colSpan={7} className="text-center text-slate-500 py-8">로딩중...</td>
                            </tr>
                          ) : voucherBookRules.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-slate-500 py-8">등록된 무료쿠폰북 규칙이 없습니다.</td>
                            </tr>
                          ) : (
                            voucherBookRules.map((rule) => (
                              <tr key={rule.id}>
                                <td className="px-4 py-3 text-sm text-slate-700">{rule.id}</td>
                                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{rule.start_date} ~ {rule.end_date}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{rule.state}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{rule.type}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{rule.stock}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{rule.site?.name || "-"}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">
                                  <div className="flex flex-wrap gap-2 min-w-max">
                                    <Button color="info" size="sm" className="rounded-lg px-3" onClick={() => openVoucherBookEventPage(rule.id)}>
                                      다운로드 링크
                                    </Button>
                                    <Button color="success" size="sm" className="rounded-lg px-3" onClick={() => openEditBookRuleModal(rule)}>
                                      수정
                                    </Button>
                                    <Button color="danger" size="sm" className="rounded-lg px-3" onClick={() => deleteBookRule(rule.id)}>
                                      삭제
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Modal isOpen={bookRuleModalOpen} onClose={() => { void confirmBookRuleClose(); }} size="4xl" variant="dialog" centered>
              <ModalHeader toggle={() => { void confirmBookRuleClose(); }}>
                {editingBookRuleId ? "무료쿠폰북 규칙 수정" : "무료쿠폰북 규칙 생성"}
              </ModalHeader>
              <ModalBody>
                <Row onChangeCapture={markBookRuleDirty} onInputCapture={markBookRuleDirty}>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">시작일</Label>
                      <CommonDatePicker value={bookStartDate} onChange={(value) => {
                        markBookRuleDirty();
                        setBookStartDate(value);
                      }} className="form-control" options={{
                      }} />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">종료일</Label>
                      <CommonDatePicker value={bookEndDate} onChange={(value) => {
                        markBookRuleDirty();
                        setBookEndDate(value);
                      }} className="form-control" options={{
                      }} />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">상태</Label>
                      <select value={bookState} onChange={(e) => setBookState(e.target.value)} className="form-select">
                        <option value="노출중">노출중</option>
                        <option value="숨김">숨김</option>
                      </select>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">수량</Label>
                      <Input type="number" className="form-control" value={bookStock} min={1} onChange={(e) => setBookStock(Number(e.target.value) || 1)} />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">타입</Label>
                      <select value={bookType} onChange={(e) => setBookType(e.target.value)} className="form-select" disabled>
                        <option value="event">이벤트</option>
                        <option value="new_join">신규 회원가입</option>
                        <option value="membership">맴버십 회원</option>
                        <option value="usage_amount">기간 사용금액</option>
                      </select>
                      <p className="mt-2 text-xs text-slate-500">현재 사이트 이벤트 배포로 고정됩니다.</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">대상 사이트</Label>
                      <Select isDisabled options={eventSites} className="basic-select" classNamePrefix="select" value={selectedBookSite} />
                    </div>
                  </Col>
                </Row>
                <div className="flex justify-end gap-2">
                  <Button color="light" onClick={() => { void confirmBookRuleClose(); }}>취소</Button>
                  <Button color="success" onClick={saveBookRule}>
                    {editingBookRuleId ? "수정" : "생성"}
                  </Button>
                </div>
              </ModalBody>
            </Modal>
          </>
        ) : null}
      </Container>
    </div>
  </React.Fragment>);
};
export default VoucherForm;
